/**
 * Gift with purchase: auto-adds a free gift when qualifying items reach a
 * minimum spend, and takes it back out if the cart drops below it.
 *
 * Settings live in the "Gift with purchase" section (theme editor). The $0
 * price is applied by the automatic discount in Shopify admin; this only
 * manages the cart line.
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const SOURCE = 'gift-with-purchase';
const PROPERTY = '_gift_with_purchase';

function readConfig() {
  const el = document.getElementById('gwp-config');
  if (!el) return null;
  try {
    const config = JSON.parse(el.textContent || '{}');
    if (!config.enabled || !config.variantId || !config.thresholdCents) return null;
    config.qualifyingIds = (config.qualifyingIds || []).map(Number);
    return config;
  } catch (error) {
    console.error('[gift-with-purchase] bad config', error);
    return null;
  }
}

const config = readConfig();
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const nativeFetch = window.fetch.bind(window);
const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?\/?$/;
let running = null;
let queued = false;
let debounceTimer = null;

function storageGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function storageSet(key, value) {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch (e) {
    /* storage unavailable */
  }
}

async function postJson(url, body) {
  const response = await nativeFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart request failed');
  return data;
}

async function getCart() {
  const response = await nativeFetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  return response.json();
}

function qualifyingTotal(cart) {
  return (cart.items || [])
    .filter((item) => config.qualifyingIds.includes(Number(item.product_id)))
    .reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
}

/** Auto-added gift lines, with their 1-based line numbers. */
function giftLines(cart) {
  const lines = [];
  (cart.items || []).forEach((item, index) => {
    if (Number(item.variant_id) === Number(config.variantId) && item.properties && item.properties[PROPERTY]) {
      lines.push({ item, line: index + 1 });
    }
  });
  return lines;
}

/** Remove auto-added gift quantity above `keep`, working from the last line up. */
async function trimGift(cart, keep) {
  let changed = null;
  let remaining = keep;
  const lines = giftLines(cart);
  const plan = lines.map(({ item, line }) => {
    const allowed = Math.min(item.quantity, remaining);
    remaining -= allowed;
    return { line, quantity: allowed, current: item.quantity };
  });
  for (const step of plan.reverse()) {
    if (step.quantity !== step.current) {
      changed = await postJson(`${root}cart/change.js`, { line: step.line, quantity: step.quantity });
    }
  }
  return changed;
}

function announce(cart) {
  document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, { source: SOURCE, itemCount: cart.item_count }));
}

async function doSync() {
  try {
    let cart = await getCart();
    const qualifies = qualifyingTotal(cart) >= config.thresholdCents;
    const lines = giftLines(cart);
    const giftQty = lines.reduce((sum, l) => sum + l.item.quantity, 0);
    const ownGiftLine = (cart.items || []).some(
      (item) => Number(item.variant_id) === Number(config.variantId) && !(item.properties && item.properties[PROPERTY])
    );
    const addedKey = `gwp-added:${cart.token}`;
    const declinedKey = `gwp-declined:${cart.token}`;

    if (qualifies) {
      if (giftQty > 1) {
        const updated = await trimGift(cart, 1);
        if (updated) announce(updated);
        return;
      }
      if (giftQty === 1) return;
      // Customer already added the bag themselves: the discount makes it free.
      if (ownGiftLine) return;
      // Customer removed the auto-added gift: respect that for this cart.
      if (storageGet(declinedKey) === '1') return;

      await postJson(`${root}cart/add.js`, {
        items: [{ id: Number(config.variantId), quantity: 1, properties: { [PROPERTY]: 'Yes' } }],
      });
      storageSet(addedKey, '1');

      // Guard against a second tab or page load adding at the same moment.
      cart = await getCart();
      const updated = (await trimGift(cart, 1)) || cart;
      announce(updated);
      return;
    }

    storageSet(declinedKey, null);
    if (giftQty > 0 && config.autoRemove) {
      const updated = await trimGift(cart, 0);
      storageSet(addedKey, null);
      if (updated) announce(updated);
    }
  } catch (error) {
    console.error('[gift-with-purchase]', error);
  }
}

/** Runs one check at a time; a request made mid-check queues one more pass. */
function sync() {
  if (!config) return Promise.resolve();
  if (running) {
    queued = true;
    return running;
  }
  running = doSync().finally(() => {
    running = null;
    if (queued) {
      queued = false;
      sync();
    }
  });
  return running;
}

/** Waits until every queued check has finished. */
async function settle() {
  while (running) await running;
}

/** Notice when a customer removes the auto-added gift while still qualifying. */
async function checkDeclined() {
  try {
    const cart = await getCart();
    const addedKey = `gwp-added:${cart.token}`;
    if (storageGet(addedKey) !== '1') return;
    if (giftLines(cart).length) return;
    if (qualifyingTotal(cart) >= config.thresholdCents) storageSet(`gwp-declined:${cart.token}`, '1');
    storageSet(addedKey, null);
  } catch (e) {
    /* ignore */
  }
}

function scheduleSync() {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(async () => {
    await checkDeclined();
    sync();
  }, 250);
}

function isCartMutation(url) {
  try {
    return CART_MUTATION.test(new URL(String(url), window.location.href).pathname);
  } catch (e) {
    return false;
  }
}

if (config) {
  // 1. Theme cart events (add to cart, quick add, cart drawer changes).
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const detail = event.detail || {};
    if (detail.sourceId === SOURCE || (detail.data && detail.data.source === SOURCE)) return;
    scheduleSync();
  });

  // 2. Any other code or app that changes the cart without telling the theme.
  window.fetch = function (input, init) {
    const request = nativeFetch(input, init);
    const url = typeof input === 'string' || input instanceof URL ? input : input && input.url;
    if (isCartMutation(url)) request.then(scheduleSync, scheduleSync);
    return request;
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isCartMutation(url)) this.addEventListener('loadend', scheduleSync);
    return nativeOpen.apply(this, arguments);
  };

  // 3. Every page load, including back/forward navigation.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) sync();
  });

  // 4. Last check before checkout: hold the checkout submit until the cart is right.
  let checkoutReady = false;
  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || checkoutReady) return;
      const submitter = event.submitter;
      const toCheckout =
        (submitter && submitter.getAttribute('name') === 'checkout') || /\/checkout/.test(form.action || '');
      if (!toCheckout) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      clearTimeout(debounceTimer);
      await Promise.race([
        (async () => {
          await checkDeclined();
          await sync();
          await settle();
        })(),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
      checkoutReady = true;
      if (submitter && typeof form.requestSubmit === 'function') form.requestSubmit(submitter);
      else form.submit();
    },
    true
  );

  sync();
}
